import { z } from 'zod';
/**
 * Client form on the kiosk (owner 15.10.2026, replaces the per-question draft of 14.10).
 *
 * Health data (GDPR art. 9) is kept to a minimum:
 *  - KEPT (encrypted, 24 months): name, date of birth, preferences (contact, pressure, how they
 *    heard of us, similar treatments before), the signature, and only the OUTCOME of the health
 *    part — "none of the listed conditions" or "declared" — plus the therapist's decision.
 *  - SAME DAY ONLY (encrypted, deleted at the end of the visit day): WHICH conditions were ticked,
 *    the notes, the aesthetic procedures (injections / surgery) and "anything else".
 * Never sent to the CRM, never logged.
 */
export const QUESTIONNAIRE_NOTE_MAX = 200;
export const QUESTIONNAIRE_OTHER_MAX = 300;
/** Signature drawn with a finger, PNG data URL. ~600×200 strokes stay far below this. */
export const SIGNATURE_MAX = 150_000;
/** Kept for the old (14.10 draft) records still on the server. */
export const QUESTIONNAIRE_DETAIL_MAX = QUESTIONNAIRE_NOTE_MAX;
export const CONTACT_METHODS = ['email', 'whatsapp', 'telegram', 'phone'];
/** Owner 19.10.2026: + TikTok and AI assistants (ChatGPT, Claude…) — append only, stored values never change. */
export const HEARD_FROM = [
    'instagram',
    'tiktok',
    'google',
    'ai_assistant',
    'friend',
    'classpass',
    'passing_by',
    'other',
];
const DateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const QuestionnaireFormRequestSchema = z
    .object({
    version: z.string().regex(/^[a-z0-9-]{1,40}$/),
    fullName: z.string().trim().min(2).max(80),
    birthDate: DateSchema,
    contact: z.enum(CONTACT_METHODS),
    pressure: z.number().int().min(1).max(5),
    heardFrom: z.enum(HEARD_FROM),
    similarBefore: z.boolean(),
    conditions: z.object({
        none: z.boolean(),
        ids: z
            .array(z.string().regex(/^[a-z0-9-]{1,40}$/))
            .max(30)
            .default([]),
        note: z.string().trim().max(QUESTIONNAIRE_NOTE_MAX).optional(),
    }),
    aesthetic: z.object({
        had: z.boolean(),
        detail: z.string().trim().max(QUESTIONNAIRE_NOTE_MAX).optional(),
    }),
    otherNote: z.string().trim().max(QUESTIONNAIRE_OTHER_MAX).optional(),
    /** Must be ticked by the client; without it nothing is stored. */
    consent: z.literal(true),
    signature: z
        .string()
        .max(SIGNATURE_MAX)
        .regex(/^data:image\/png;base64,[A-Za-z0-9+/]+=*$/),
})
    .refine((r) => (r.conditions.none ? r.conditions.ids.length === 0 : r.conditions.ids.length > 0 || !!r.conditions.note), {
    message: 'conditions',
    path: ['conditions'],
})
    .refine((r) => !r.aesthetic.had || (r.aesthetic.detail ?? '').length >= 2, {
    message: 'aesthetic detail',
    path: ['aesthetic', 'detail'],
});
/** Valid calendar date, age 14–110 on `today` (YYYY-MM-DD). */
export function isPlausibleBirthDate(birthDate, today) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate);
    if (!m)
        return false;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const dt = new Date(Date.UTC(y, mo - 1, d));
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d)
        return false;
    const [ty, tm, td] = today.split('-').map(Number);
    let age = ty - y;
    if (tm < mo || (tm === mo && td < d))
        age -= 1;
    return age >= 14 && age <= 110;
}
/** Checks a submitted form against its version; condition ids must exist, duplicates removed. */
export function checkQuestionnaireForm(def, req, today) {
    if (req.version !== def.version)
        return { ok: false, code: 'QUESTIONNAIRE_VERSION_MISMATCH' };
    if (!isPlausibleBirthDate(req.birthDate, today))
        return { ok: false, code: 'QUESTIONNAIRE_INCOMPLETE' };
    // v4 (simple): only the confirmation "none applies" exists
    if (def.simple && (!req.conditions.none || req.aesthetic.had))
        return { ok: false, code: 'QUESTIONNAIRE_INCOMPLETE' };
    const known = new Set(def.conditions.map((c) => c.id));
    if (req.conditions.ids.some((id) => !known.has(id)))
        return { ok: false, code: 'QUESTIONNAIRE_INCOMPLETE' };
    const ids = def.conditions.map((c) => c.id).filter((id) => req.conditions.ids.includes(id));
    return { ok: true, conditionIds: ids };
}
export const TherapistDecisionRequestSchema = z.object({
    decision: z.enum(['proceed', 'decline']),
    /** Tablet language for the note written to the CRM booking on "decline" (owner 21.10.2026). */
    lang: z.enum(['pt', 'en', 'ru']).optional(),
});
/** "Yes" answers first (old records). */
export function yesFirst(items) {
    return [...items.filter((i) => i.yes), ...items.filter((i) => !i.yes)];
}
//# sourceMappingURL=questionnaire.js.map